import type { Entries } from './catalog';
import { actions } from './actions';
import { journeys } from './journeys';
import { credentials } from './credentials';
import { factors } from './factors';
import { invites } from './invites';
import { members } from './members';
import { home } from './home';
import { consent } from './consent';
import { connect } from './connect';
import { permissions } from './permissions';
import { outage } from './outage';

/**
 * Español.
 *
 * Es una traducción, no una variante: dice lo mismo que el inglés, con la misma voz sobria y las mismas
 * distinciones. Donde el inglés separa «caducada» de «ya utilizada», el español también, porque son dos
 * situaciones distintas para quien las lee.
 *
 * El tratamiento regional es neutro y el tuteo es deliberado; no se adopta en silencio la decisión
 * regional de otro producto de la familia. Cuál es el español de lanzamiento sigue siendo una decisión
 * abierta, registrada en `docs/design.md`.
 */
export const es: Entries = {
	'action.signout.everywhere': 'Cerrar sesión en todos los dispositivos',
	'action.cancel': 'Cancelar',
	show: 'Mostrar',
	'action.save': 'Guardar',
	'action.saving': 'Guardando…',
	'action.create': 'Crear',
	'action.back': 'Volver',
	'action.retry': 'Reintentar',
	'action.copy': 'Copiar',
	'action.copied': 'Copiado',
	'action.close': 'Cerrar',

	'nav.overview': 'Resumen',
	'nav.organizations': 'Organizaciones',
	'nav.account': 'Tu cuenta',
	'nav.security': 'Acceso y seguridad',
	'nav.activity': 'Actividad de seguridad',
	'nav.tokens': 'Tokens de acceso',
	'nav.skip': 'Ir al contenido principal',
	'nav.group.account': 'Cuenta',

	'return.action': 'Volver a {product}',
	'switch.working': 'Cambiando de persona…',
	'switch.disabled': 'Cambiar de persona solo está disponible en una instalación de desarrollo local habilitada.',
	'switch.failed': 'No se pudo iniciar sesión como esa persona.',
	'switch.retry': 'Intentar de nuevo',
	'quick.title': 'Acceso rápido (local)',
	'quick.lead': 'Solo desarrollo local. Elige una persona local para entrar como ella; quien haya iniciado sesión aquí sale primero.',
	'quick.none': 'Sin organización todavía',
	'quick.unanswered': 'El asistente de personas locales no respondió',
	'quick.restart': 'Vuelve a iniciar el entorno local.',
	'quick.back': 'Volver a iniciar sesión',
	'quick.link': 'Acceso rápido (local)',
	'leave.working': 'Cerrando la sesión de Beyond…',
	'leave.done': 'Cerraste la sesión de Beyond. Terminó cada sesión de producto que venía de este inicio de sesión.',
	'leave.signin': 'Iniciar sesión de nuevo',
	'leave.failed': 'No se completó el cierre de la sesión de Beyond, así que todas las sesiones siguen abiertas. Inténtalo de nuevo.',
	'leave.retry': 'Intentar de nuevo',

	'state.loading': 'Cargando…',
	'state.failed': 'No se hizo y no cambió nada. Inténtalo de nuevo.',
	'state.denied': 'No tienes permiso para hacer eso aquí. No se cambió nada.',
	'state.notfound': 'Esto no está aquí.',
	'state.expired': 'Tu sesión caducó. Inicia sesión para continuar.',
	'state.revoked': 'Tu sesión terminó. Inicia sesión para continuar.',
	'state.suspended': 'Esta cuenta no puede iniciar sesión. Contacta a quien la administra.',
	'state.unavailable': 'Beyond Accounts no respondió. No se cambió nada.',
	'state.unknown': 'Puede que el cambio se haya hecho. Beyond Accounts no respondió. Compruébalo antes de volver a intentarlo.',
	'state.upstream': 'El proveedor de identidad no responde en este momento. Reintenta en un momento.',
	'state.unsent': 'No se pudo contactar con Beyond Accounts. No se cambió nada.',
	'state.limited': 'Demasiados intentos. Espera {seconds} segundos y vuelve a intentarlo.',
	'state.retrying': 'Reintentando…',
	'state.saved': 'Guardado.',
	'state.savedStale': 'Guardado. No se pudo actualizar esta página, así que lo que ves puede estar desactualizado.',

	'signin.title': 'Iniciar sesión',
	'signin.lead': 'Una cuenta para todos los productos de Beyond.',
	'signin.email': 'Correo electrónico',
	'signin.password': 'Contraseña',
	'signin.submit': 'Iniciar sesión con correo',
	'signin.with': 'Continuar con {provider}',
	'signin.unavailable': 'Iniciar sesión no está disponible ahora mismo.',
	'signin.provider.unreachable': 'El inicio de sesión no responde ahora mismo. Vuelve a intentarlo en un momento.',
	'signin.refused': 'Esos datos no iniciaron tu sesión.',
	'signin.language': 'Idioma',
	'signin.returning': 'Volverás a {product}.',
	'signin.working': 'Iniciando tu sesión…',

	'account.title': 'Tu cuenta',
	'account.lead': 'Tu nombre, idioma y apariencia en Beyond.',
	'account.details': 'Tus datos',
	'account.name': 'Nombre',
	'account.name.help': 'Lo que ven los demás miembros de tus organizaciones.',
	'account.preferences': 'Idioma y apariencia en todo Beyond',
	'account.preferences.lead': 'Todos los productos de Beyond los usan.',
	'account.preferences.unsaved': 'Este dispositivo usa tu elección, pero no se guardó en tu cuenta, así que tus otros dispositivos y productos siguen con la anterior. Vuelve a elegir para reintentar.',
	'account.language': 'Idioma',
	'account.appearance': 'Apariencia',
	'appearance.system': 'Seguir al sistema',
	'appearance.light': 'Claro',
	'appearance.dark': 'Oscuro',

	'identities.title': 'Formas de iniciar sesión',
	'identities.lead': 'Cada uno te da acceso a esta cuenta.',
	'identities.provider.github': 'GitHub',
	'identities.provider.google': 'Google',
	'identities.provider.email': 'Correo',
	'identities.attach': 'Agregar {provider}',
	'signin.recover': '¿Olvidaste la contraseña?',
	'signin.recover.lead': 'Escribe la dirección con la que inicias sesión. Si tiene cuenta aquí, te llega un mensaje.',
	'signin.recover.submit': 'Enviar mensaje de recuperación',
	'signin.recover.sent': 'Si esa dirección tiene cuenta aquí, ya va en camino un mensaje de recuperación.',
	'identities.attach.lead': 'Añade una forma de acceso a esta cuenta. Esto no crea otra cuenta.',
	'identities.attached': 'Ese método de inicio de sesión quedó vinculado a esta cuenta.',
	'reset.title': 'Elige una contraseña nueva',
	'reset.lead': 'Este enlace sirve una sola vez. Al establecer la contraseña inicias sesión.',
	'reset.password': 'Contraseña nueva',
	'reset.again': 'Repítela',
	'reset.submit': 'Establecer la contraseña',
	'reset.mismatch': 'Las dos no coinciden.',
	'reset.expired': 'Ese enlace ya no es válido. Pide otro.',
	'confirm.title': 'Confirmando tu dirección',
	'confirm.lead': 'Un momento mientras se termina tu cuenta.',
	'confirm.expired': 'Ese enlace ya no es válido.',
	'confirm.again': 'Ir a iniciar sesión',
	'identities.detach.item': 'Quitar…',
	'identities.linked': 'Agregada el {date}',
	'identities.unverified': 'Dirección sin confirmar',
	'identities.last': 'Es la única forma de entrar a esta cuenta, así que no se puede quitar. Agrega otra primero.',
	'identities.inuse': 'Esa identidad ya pertenece a otra cuenta.',
	'identities.reauth': 'Inicia sesión de nuevo para cambiar cómo inicias sesión.',

	'legal.label': 'Legal',
	'legal.privacy': 'Política de privacidad',
	'legal.terms': 'Términos del servicio',
	'legal.notice': 'Al continuar aceptas los términos del servicio y reconoces la política de privacidad.',

	'data.title': 'Tus datos',
	'data.lead': 'Descarga una copia de lo que Beyond Accounts guarda sobre ti, o elimina tu cuenta.',
	'data.policy': 'Cómo se tratan tus datos',
	'data.copy': 'Descargar una copia',
	'data.copy.lead': 'Un archivo JSON con tu perfil, tus formas de iniciar sesión, tus organizaciones, tus sesiones y los registros sobre tu cuenta. No incluye credenciales ni datos de otras personas.',
	'data.copied': 'Se descargó tu copia.',
	'data.erase': 'Eliminar cuenta',
	'data.erase.title': 'Eliminar esta cuenta',
	'data.erase.lead': 'Tu cuenta se elimina dentro de 14 días. Todas las sesiones terminan ahora; hasta entonces puedes iniciar sesión y conservarla.',
	'data.erase.word': 'ELIMINAR',
	'data.erase.confirm': 'Escribe {word} para confirmar',
	'data.erase.submit': 'Eliminar mi cuenta',
	'data.reauth': 'Para protegerte, esto requiere un inicio de sesión reciente. Vuelve a iniciar sesión y regresa aquí.',
	'data.reauth.action': 'Volver a iniciar sesión',
	'signin.erased': 'Tu cuenta se eliminará dentro de 14 días. Inicia sesión antes para conservarla.',

	'organizations.title': 'Organizaciones',
	'organizations.lead': 'Tus organizaciones y roles.',
	'organizations.none.title': 'Todavía no estás en una organización',
	'organizations.none.body': 'Crea una, o acepta una invitación que alguien te envíe. Puedes usar tu cuenta sin ninguna.',
	'organizations.create': 'Crear organización',
	'organizations.create.title': 'Crear una organización',
	'organizations.name': 'Nombre',
	'organizations.name.help': 'Puedes cambiarlo después.',
	'organizations.created': 'Organización creada. Eres su propietario.',

	'settings.title': 'Ajustes',
	'settings.save.anyway': 'Guardar de todos modos',

	'role.owner': 'Propietario',
	'role.admin': 'Administrador',
	'role.developer': 'Desarrollador',
	'role.viewer': 'Lector',
	'role.help.owner': 'Todo, incluida la propiedad misma.',
	'role.help.admin': 'Membresías e invitaciones.',
	'role.help.developer': 'Un miembro que trabaja aquí.',
	'role.help.viewer': 'Solo lectura.',
	'role.note': 'Un rol aquí decide la membresía. Lo que alguien puede hacer en un producto lo decide ese producto.',

	'team.title': 'Equipo',
	'members.title': 'Miembros',
	'members.count': { one: 'Un miembro', other: '{count} miembros' },
	'members.unnamed': 'Sin nombre',
	'members.role': 'Rol',
	'members.removed': 'Quitado',
	'members.remove.title': '¿Quitar a {name}?',
	'members.remove.body': 'Pierde su membresía de {organization}.',
	'members.lastowner': 'Una organización conserva al menos un propietario. Haz propietario a alguien más primero.',

	'invitations.title': 'Invitaciones',
	'invitations.lead': 'Cada invitación pertenece a una dirección, caduca y funciona una vez.',
	'invitations.send': 'Invitar miembro',
	'invitations.send.title': 'Invitar a alguien',
	'invitations.email': 'Correo electrónico',
	'invitations.email.help': 'Debe iniciar sesión con esta dirección, confirmada por su proveedor, para aceptar.',
	'invitations.role': 'Rol',
	'invitations.none': 'No hay invitaciones.',
	'invitations.pending': 'En espera',
	'invitations.accepted': 'Aceptada',
	'invitations.revoked': 'Retirada',
	'invitations.expires': 'Caduca el {date}',
	'invitations.revoke': 'Retirar',
	'invitations.link': 'Enlace de invitación',
	'invitations.link.once': 'Este enlace se muestra una sola vez y no se guarda. Cópialo ahora y envíaselo a {email}.',
	'invitations.replaced': 'Esto reemplaza la invitación que ya estaba abierta para esta dirección.',
	'invitations.created': 'Invitación creada. Queda en espera hasta que el enlace se use o caduque.',

	'invitation.title': 'Unirte a {organization}',
	'invitation.lead': 'Te invitaron como {role}.',
	'invitation.accept': 'Aceptar invitación',
	'invitation.signin': 'Inicia sesión para aceptar',
	'invitation.address': 'Inicia sesión con {email} para aceptar esto.',
	'invitation.unknown': 'Esa invitación no es válida.',
	'invitation.expired': 'Esa invitación caducó. Pide una nueva.',
	'invitation.closed': 'Esa invitación ya se usó o se retiró.',
	'invitation.unverified': 'Inicia sesión con la dirección a la que se envió esta invitación, confirmada por tu proveedor.',

	'products.title': 'Productos',
	'products.lead': 'Ábrelos sin volver a iniciar sesión.',

	'error.unexpected': 'No se pudo hacer y no se cambió nada. Inténtalo de nuevo.',
	'error.network': 'No se pudo contactar a Accounts. Revisa tu conexión y reintenta.',
	'error.validation': 'Revisa lo que escribiste.',
	'error.field.email': 'Escribe un correo electrónico válido.',

	'language.en': 'English',
	'language.es': 'Español',

	'nav.notifications': 'Notificaciones',
	'notifications.title': 'Notificaciones',

	...actions.es,
	...journeys.es,
	...credentials.es,
	...factors.es,
	...invites.es,
	...members.es,
	...home.es,
	...consent.es,
	...connect.es,
	...permissions.es,
	...outage.es
};
